import { useState, useMemo } from 'react';

import CommonImage from '@/components/CommonImage';
import CreateAndEditDashboard from '@/features/task-configuration/components/CreateAndEditDashboard';
import DashboardCard from '@/features/task-configuration/components/DashboardCard';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';
import { DASHBOARD_TAB_ITEMS, DASHBOARD_SECTIONS } from '@/features/task-configuration/constants';
import { useGetUserByAllTypeQuery } from '@/api/commonApis';
import { replaceUnderLine } from '@/utils/Util';
import {
	useGetDashboardCardByUserTypeQuery,
	useRemoveTaskDashboardCardMutation,
	useRemoveTaskDashboardFilterMutation
} from '@/features/task-configuration/api/taskConfigurationApi';
import { toastError, toastSuccess } from '@/components/Toast';
import DashboardSectionTab from '@/features/task-configuration/components/DashboardSectionTab';
import NoData from '@/components/NoData';
import LoaderComponent from '@/components/LoaderComponent';

const DashboardConfiguration = () => {
	// const [selectedCard, setSelectedCard] = useState(null);
	const [searchKey, setSearchKey] = useState('');
	const [isExpanded, setIsExpanded] = useState(true);
	const [isOpenCreateDashboardModal, setIsOpenCreateDashboardModal] = useState(false);
	const [selectedCardToEdit, setSelectedCardToEdit] = useState(null);
	const [taskActiveTab, setTaskActiveTab] = useState(DASHBOARD_TAB_ITEMS.OTHERS_PENDING_TASK);
	const [cardToDelete, setCardToDelete] = useState(null);
	const { data: userTypeList = [], isLoading: usersLoading } = useGetUserByAllTypeQuery();
	const [selectedUserType, setSelectedUserType] = useState('ACCOUNT_MANAGER');
	const [roleSearch, setRoleSearch] = useState('');
	const { data: dashboardCardList = [], isLoading: dashboardLoading } = useGetDashboardCardByUserTypeQuery({
		userType: selectedUserType
	});
	const [removeTaskDashboardCard] = useRemoveTaskDashboardCardMutation();
	const [removeTaskDashboardFilter] = useRemoveTaskDashboardFilterMutation();

	// Group dashboard cards by section
	const groupedDashboardCards = useMemo(() => {
		const grouped = {};

		// Initialize groups for all sections
		DASHBOARD_SECTIONS.forEach((section) => {
			grouped[section.id] = {
				sectionInfo: section,
				cards: []
			};
		});

		// Group cards by their section constant
		dashboardCardList.forEach((card) => {
			const sectionConstant = card?.section?.constant;
			if (sectionConstant && grouped[sectionConstant]) {
				grouped[sectionConstant].cards.push(card);
			}
		});

		return grouped;
	}, [dashboardCardList]);

	const handleEditCard = (card) => {
		setSelectedCardToEdit(card);
		setIsOpenCreateDashboardModal(true);
	};

	const handleDeleteCard = (card) => {
		setCardToDelete(card);
	};

	const handleConfirmDelete = async () => {
		try {
			// Delete associated filters first
			if (cardToDelete?.filterIds && cardToDelete.filterIds.length > 0) {
				const filterDeletePromises = cardToDelete.filterIds.map((filterId) =>
					removeTaskDashboardFilter({ id: filterId }).unwrap()
				);
				await Promise.all(filterDeletePromises);
			}

			// Then delete the dashboard card
			await removeTaskDashboardCard({ id: cardToDelete?.id }).unwrap();
			toastSuccess('Dashboard card and configurations deleted successfully');
			setCardToDelete(null);
		} catch (err) {
			toastError(err?.data?.message || 'Failed to delete dashboard card');
		}
	};

	return (
		<LoaderComponent loading={dashboardLoading || usersLoading}>
			<div className="body-content pending-task-container runing-task-container bg-white">
				<div className="running-task-header d-flex align-items-center justify-content-between mb-3">
					<p className="semibold-14 gray_dark_02">Task configuration</p>
					<div className="task-action d-flex align-items-center">
						<div className="form-group size32 m-0 mr-1 search-filter filter-with-animation">
							<div className="with-icon right-icon">
								<input
									type="text"
									name="search"
									placeholder="Search"
									value={searchKey}
									onChange={(e) => setSearchKey(e.target.value)}
								/>
								<span className="right-icon">
									<CommonImage src="/icons/search.png" alt="" />
								</span>
							</div>
						</div>
						<div className="add-new-design">
							<button
								className="button text size32 px-0"
								onClick={() => setIsOpenCreateDashboardModal(true)}
							>
								<CommonImage src="/icons/add-round.svg" alt="add" />
								<span className="ml-1 gray_dark_02">Dashboard</span>
							</button>
						</div>
					</div>
				</div>
				<div className="running-task-body d-flex justify-content-between align-items-start">
					{/* Department Table */}
					<div className="department-container">
						<div className="form-group size32 m-0 mr-1 search-filter filter-with-animation mb-3">
							<div className="with-icon right-icon">
								<input
									type="text"
									name="search"
									placeholder="Search"
									value={roleSearch}
									onChange={(e) => setRoleSearch(e.target.value)}
								/>
								<span className="right-icon">
									<CommonImage src="/icons/search.png" alt="" />
								</span>
							</div>
						</div>
						<div className="running-task-table scroll-y-label">
							<table className="table with-border scroll-x-label mb-0">
								<thead>
									<tr>
										<th>SL</th>
										<th>Dashboard for</th>
									</tr>
								</thead>
								<tbody>
									{userTypeList
										?.filter((item) => item !== 'ADMIN')
										.filter((item) =>
											roleSearch
												? item.toLowerCase().includes(roleSearch.toLocaleLowerCase())
												: true
										)
										.sort((a, b) => a.localeCompare(b))
										.map((userType, index) => (
											<tr
												key={userType}
												onClick={() => setSelectedUserType(userType)}
												className={`cursor-pointer ${selectedUserType === userType ? 'active' : ''}`}
											>
												<td className="sl-column">{index + 1}</td>
												<td>{replaceUnderLine(userType)}</td>
											</tr>
										))}
								</tbody>
							</table>
						</div>
					</div>
					{/* right Section - Main Content Dashboad Cards */}
					<div className="flex-grow-1">
						<div className="pending-task-left-section">
							<div className="pending-task-header bg-white d-flex align-items-center justify-content-between">
								<div className="pending-task-summary">
									<p className="semibold-14 gray_dark_01">
										My Pending Tasks:
										<span className="gray_dark_01 ml-1">
											({groupedDashboardCards['MY_PENDING_TASKS']?.cards?.length})
										</span>
									</p>
								</div>
							</div>
							{/* Pending Tasks Sections - Display at Top */}
							<div className="all-tasks scroll-y-label">
								{groupedDashboardCards['MY_PENDING_TASKS']?.cards?.length > 0 ? (
									<div className="task-categories d-grid gap-4 grid-columns-3">
										{groupedDashboardCards['MY_PENDING_TASKS']?.cards
											?.filter((item) =>
												searchKey
													? item?.title
															?.toLowerCase()
															.includes(searchKey.toLowerCase())
													: true
											)
											?.map((card) => (
												<DashboardCard
													key={card.id}
													card={card}
													onEdit={handleEditCard}
													onDelete={handleDeleteCard}
													isShowCount={false}
													shouldDisableFilter={false}
												/>
											))}
									</div>
								) : (
									<NoData
										message={`There is no Pending task card for (${replaceUnderLine(selectedUserType)}) at this moment`}
									/>
								)}
							</div>
						</div>
						{/**-------- Tab Wrapper for Other Sections and compleated sections -----------**/}
						<div className="other-tasks-section mt-3">
							<div className="other-tasks-header bg-white">
								<div className="navigation-tab bg-white">
									<ul>
										<li
											className={`${taskActiveTab === DASHBOARD_TAB_ITEMS.OTHERS_PENDING_TASK ? 'active' : ''}`}
											onClick={() =>
												setTaskActiveTab(DASHBOARD_TAB_ITEMS.OTHERS_PENDING_TASK)
											}
										>
											<p>
												Pending with Others: (
												{groupedDashboardCards['OTHERS_PENDING_TASKS']?.cards?.length}
												)
											</p>
										</li>
										<li
											className={`${taskActiveTab === DASHBOARD_TAB_ITEMS.COMPLETED_TASK ? 'active' : ''}`}
											onClick={() =>
												setTaskActiveTab(DASHBOARD_TAB_ITEMS.COMPLETED_TASK)
											}
										>
											<p>
												Completed task: (
												{groupedDashboardCards['COMPLETED_TASKS']?.cards?.length})
											</p>
										</li>
									</ul>
								</div>
							</div>
							{/* Other pending task tab */}
							{taskActiveTab === DASHBOARD_TAB_ITEMS.OTHERS_PENDING_TASK &&
								(groupedDashboardCards['OTHERS_PENDING_TASKS']?.cards?.length > 0 ? (
									<DashboardSectionTab
										isExpanded={isExpanded}
										setIsExpanded={setIsExpanded}
										cards={groupedDashboardCards['OTHERS_PENDING_TASKS']?.cards?.filter(
											(item) =>
												searchKey
													? item?.title
															?.toLowerCase()
															.includes(searchKey.toLowerCase())
													: true
										)}
										handleEditCard={handleEditCard}
										handleDeleteCard={handleDeleteCard}
										isShowCount={false}
										shouldDisableFilter={false}
									/>
								) : (
									<NoData
										message={`There is no Others pending task card for (${replaceUnderLine(selectedUserType)}) at this moment`}
									/>
								))}
							{/* Compleated task tab */}
							{taskActiveTab === DASHBOARD_TAB_ITEMS.COMPLETED_TASK &&
								(groupedDashboardCards['COMPLETED_TASKS']?.cards?.length > 0 ? (
									<DashboardSectionTab
										isExpanded={isExpanded}
										setIsExpanded={setIsExpanded}
										cards={groupedDashboardCards['COMPLETED_TASKS']?.cards?.filter(
											(item) =>
												searchKey
													? item?.title
															?.toLowerCase()
															.includes(searchKey.toLowerCase())
													: true
										)}
										handleEditCard={handleEditCard}
										handleDeleteCard={handleDeleteCard}
										isShowCount={false}
										shouldDisableFilter={false}
									/>
								) : (
									<NoData
										message={`There is no Completed task card for (${replaceUnderLine(selectedUserType)}) at this moment`}
									/>
								))}
						</div>
					</div>
				</div>
			</div>
			{/* Modals */}
			{isOpenCreateDashboardModal && (
				<CreateAndEditDashboard
					onClose={() => {
						setIsOpenCreateDashboardModal(false);
						setSelectedCardToEdit(null);
					}}
					selectedCardToEdit={selectedCardToEdit}
					selectedUserType={selectedUserType}
				/>
			)}
			{cardToDelete && (
				<DeleteConfirmationModal
					onClose={() => setCardToDelete(null)}
					onConfirm={handleConfirmDelete}
					title="Delete Dashboard Card"
					description="Are you sure you want to delete this dashboard card? This will also delete all associated filter configurations."
				/>
			)}
		</LoaderComponent>
	);
};

export default DashboardConfiguration;
